/* eslint-disable react/destructuring-assignment */
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
  renderRichText,
} from 'gatsby-source-contentful/rich-text';
import { ReactElement, ReactNode } from 'react';

import { media } from 'atoms/breakpoints/breakpoints';
import color from 'atoms/colors/colors';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image, { ImageData } from 'quarks/Image';
import Link from 'quarks/Link';
import Text from 'quarks/Text';

import ComponentButton from 'components/Button/ComponentButton';
import ListItem from 'components/ListItem/ListItem';
import ButtonWrapper from 'components/RichText/ButtonWrapper';
import RichTextParagraphBlock from 'components/RichText/RichTextParagraphBlock';
import ComponentTestimonial from 'components/Testimonial/Testimonial';

import { stringToKebabCase } from 'utils/functions';
import wrapRichTextElements from 'utils/richTextWrapper';

import type { CommonNode } from '@contentful/rich-text-react-renderer';
import type { Maybe, Scalars } from 'graphqlTypes';

const scrollPosition = 'scroll-margin-top: 150px;';

export type ContentfulRichText = {
  __typename?: string;
  raw?: Maybe<Scalars['String']>;
  references?: Maybe<Array<Maybe<unknown>>>;
};

export type RichText = RenderRichTextData<ContentfulRichTextGatsbyReference>;

const getId = children => {
  const idString = children
    .map(child => (typeof child === 'string' ? child?.toString() : child?.props?.children?.toString() || ''))
    ?.join('')
    ?.trim();

  return stringToKebabCase(idString);
};

const richTextParser = <T extends ContentfulRichText>(content: T) => {
  const options = {
    renderText: (text: ReactNode) => text,
    renderMark: {
      [MARKS.BOLD]: (text: ReactNode) => <Text fontWeight="bold">{text}</Text>,
      [MARKS.ITALIC]: (text: ReactNode) => <Text fontStyle="italic">{text}</Text>,
      [MARKS.UNDERLINE]: (text: ReactNode) => <Text textDecoration="underline">{text}</Text>,
      [MARKS.CODE]: (text: ReactNode) => <Text>{text}</Text>,
      [MARKS.SUBSCRIPT]: (text: ReactNode) => <sub>{text}</sub>,
      [MARKS.SUPERSCRIPT]: (text: ReactNode) => <sup>{text}</sup>,
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: CommonNode, children: ReactNode) => {
        if (Array.isArray(children) && children.length < 2 && children[0].length === 0) {
          return null;
        }

        return <RichTextParagraphBlock node={node}>{children}</RichTextParagraphBlock>;
      },
      [BLOCKS.HEADING_1]: (node: CommonNode, children: ReactNode) => (
        <Heading as="h1" id={getId(children)} css={scrollPosition}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_2]: (node: CommonNode, children: ReactNode) => (
        <Heading as="h2" id={getId(children)} css={scrollPosition}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_3]: (node: CommonNode, children: ReactNode) => (
        <Heading as="h3" id={getId(children)} css={scrollPosition}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_4]: (node: CommonNode, children: ReactNode) => (
        <Heading as="h4" id={getId(children)} css={scrollPosition}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_5]: (node: CommonNode, children: ReactNode) => (
        <Heading as="h5" id={getId(children)} css={scrollPosition}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_6]: (node: CommonNode, children: ReactNode) => (
        <Heading as="h6" id={getId(children)} css={scrollPosition}>
          {children}
        </Heading>
      ),
      [BLOCKS.UL_LIST]: (node: CommonNode, children: ReactNode) => (
        <Flex
          as="ul"
          flexDirection="column"
          gap="0.5rem"
          css={`
            list-style: none;
            padding-left: 0;
            li {
              position: relative;
              padding-left: 20px;
              &::before {
                content: '';
                position: absolute;
                left: 0;
                top: calc(50% - 3px);
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background-color: ${color.green[600]};
              }
            }
          `}
        >
          {children}
        </Flex>
      ),
      [BLOCKS.OL_LIST]: (node: CommonNode, children: ReactNode) => (
        <Flex
          as="ol"
          flexDirection="column"
          gap="0.5rem"
          css={`
            list-style: none;
            padding-left: 0;
            li {
              position: relative;
              padding-left: 20px;
              &::before {
                content: '';
                position: absolute;
                left: 0;
                top: calc(50% - 3px);
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background-color: ${color.green[600]};
              }
            }
          `}
        >
          {children}
        </Flex>
      ),
      [BLOCKS.QUOTE]: (node: CommonNode, children: ReactNode) => (
        <Flex className="blockquote" borderLeft="2px solid" borderColor="navy-700" paddingLeft={24} marginY={16}>
          {children}
        </Flex>
      ),
      [BLOCKS.TABLE]: (node: CommonNode, children: ReactNode) => (
        <Flex
          className="table"
          width="100%"
          css={`
            width: 100%;
            overflow: auto;

            table {
              border-spacing: 0;
              width: 100%;
            }

            th {
              background: #737475;
              padding: 15px;

              p {
                color: white;
                font-size: 16px;
                line-height: 22px;
                font-weight: 600;
                width: max-content;
                margin: 0 auto;
              }
            }
            tr {
              flex-wrap: wrap;
            }

            td {
              padding: 15px;
              border: 0.5px solid rgb(240, 240, 240);

              p {
                margin-bottom: 10px;
                font-size: 14px;
                text-align: center;
              }
            }

            ${media.sm} {
              td {
                width: max-content;
              }
            }
          `}
        >
          <table>
            <tbody>{children}</tbody>
          </table>
        </Flex>
      ),
      [BLOCKS.EMBEDDED_ASSET]: (node: CommonNode) => {
        const { data } = node;

        return <Image image={data.target} alt={data.target.title} />;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: CommonNode) => {
        const {
          data: { target },
        } = node;
        if (!target) {
          return null;
        }

        switch (target.__typename) {
          case 'ContentfulComponentCallToAction':
            return <ComponentButton {...target} />;
          case 'ContentfulComponentTestimonial':
            return <ComponentTestimonial {...target} />;
          case 'ContentfulComponentImage':
            return (
              <Image
                width="100%"
                maxWidth="100%"
                image={(target?.bynderImageDesktop as ImageData) || (target?.imageDesktop as ImageData)}
                alt={target?.alt || 'Reference Image'}
                boxShadow={target?.boxShadow}
                roundedCorners={target?.roundedCorners}
              />
            );
          default:
            return null;
        }
      },
      [INLINES.EMBEDDED_ENTRY]: (node: CommonNode) => {
        const {
          data: { target },
        } = node;
        if (!target) {
          return null;
        }

        switch (target.__typename) {
          case 'ContentfulComponentImage':
            return (
              <Container width="400px" marginY={32}>
                <Image
                  width="100%"
                  image={(target?.bynderImageDesktop as ImageData) || (target?.imageDesktop as ImageData)}
                  alt={target?.alt || 'Reference Image'}
                  boxShadow={target?.boxShadow}
                  roundedCorners={target?.roundedCorners}
                />
              </Container>
            );
          case 'ContentfulElementListItem':
            return (
              <ListItem
                {...target}
                marginY={32}
                backgroundColor="gray-200"
                paddingAll={32}
                borderRadius="4px"
                maxWidth="100%"
                showIcon={false}
                css={`
                  > div > span {
                    margin-bottom: 15px;
                    ~ div > ul {
                      margin-left: 10px;
                    }
                  }
                `}
              />
            );
          default:
            return null;
        }
      },
      [INLINES.HYPERLINK]: (node: CommonNode, children: ReactNode) => {
        const { data } = node;

        return (
          <Link
            href={data.uri}
            textDecoration="none"
            textColor="navy-700"
            fontWeight="bold"
            hover={{ textDecoration: 'underline', textColor: 'navy-900' }}
            focus={{ textColor: 'navy-700', textDecoration: 'none' }}
          >
            {children}
          </Link>
        );
      },
    },
  };

  const componentWrappers = [
    {
      title: 'ContentfulComponentCallToAction',
      componentWrapper: ButtonWrapper,
    },
  ];

  return wrapRichTextElements(renderRichText(content as RichText, options) as ReactElement[], componentWrappers);
};

export default richTextParser;
